import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { HeroSection } from "@/components/HeroSection";
import { WhoWeServeSection } from "@/components/WhoWeServeSection";
import { WhoWeCanPaySection } from "@/components/WhoWeCanPaySection";
import { TestimonialSection } from "@/components/TestimonialSection";
import { WhoWePaySection } from "@/components/WhoWePaySection";
import { ConciergeServiceSection } from "@/components/ConciergeServiceSection";
import { PricingSection } from "@/components/PricingSection";
import { LevelUpSection } from "@/components/LevelUpSection";
import { ClientsSaySection } from "@/components/ClientsSaySection";
import { FaqSection } from "@/components/FaqSection";
import { AIConnectorDiagram } from "@/components/AIConnectorDiagram";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { appUrls } from "@/utilities/appUrls";
import heroBackground from "@/assets/Img/HeroHouseBackground.jpg";

const sendolaPayUrl = import.meta.env.VITE_SENDOLA_PAY_URL;

const ForHomeownersPage = () => {
  const [t] = useTranslation("homeowners");

  return (
    <div className="w-full flex flex-col">
      <Helmet>
        <title>{t("SEO.Title")}</title>
        <meta name="description" content={t("SEO.Description")} />
        <meta property="og:title" content={t("SEO.Title")} />
        <meta property="og:description" content={t("SEO.Description")} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="w-full bg-homeowners-bg" id="header">
        <HeroSection
          backgroundImage={heroBackground}
          layout="split"
          chip={
            <span className="inline-flex items-center gap-[9px] px-4 py-2 rounded-full bg-white/70 border border-homeowners-purple/15 backdrop-blur-[6px]">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                className="text-homeowners-green animate-[sparkSpin_3.2s_ease-in-out_infinite]"
              >
                <path
                  d="M12 2c.5 4.5 2.5 6.5 7 7-4.5.5-6.5 2.5-7 7-.5-4.5-2.5-6.5-7-7 4.5-.5 6.5-2.5 7-7z"
                  fill="currentColor"
                />
              </svg>
              <span className="font-montserrat font-semibold text-[12px] tracking-[0.12em] uppercase text-homeowners-purple">
                {t("Hero.Chip")}
              </span>
            </span>
          }
          title={
            <span className="block font-playfair font-semibold text-homeowners-headline leading-[1.14] text-[clamp(1.875rem,1rem+2.5vw,2.625rem)]">
              {t("Hero.TitleLine1")}{" "}
              <br className="hidden xl:block" />
              <span className="whitespace-nowrap">
                <span className="italic text-homeowners-green">
                  {t("Hero.TitleLine2Lead")}
                </span>
                <span className="italic text-homeowners-purple text-gradient-clip bg-[linear-gradient(100deg,#363254_0%,#8a998e_45%,#363254_90%)] bg-[length:220%_100%] animate-[aiShimmer_4s_linear_infinite]">
                  {t("Hero.TitleLine2Accent")}
                </span>
              </span>
            </span>
          }
          subtitle={
            <span className="inline-block text-homeowners-gray font-montserrat font-normal leading-[1.6] text-[clamp(1rem,0.9rem+0.5vw,1.1875rem)] xl:max-w-[510px]">
              {t("Hero.Subtitle")}{" "}
              <b className="font-semibold text-homeowners-headline">
                {t("Hero.SubtitleBold")}
              </b>
            </span>
          }
          primaryCta={{
            label: t("Hero.PrimaryCta"),
            href: appUrls.signUp,
            variant: "primary",
          }}
          secondaryCta={{
            label: t("Hero.SecondaryCta"),
            href: "/contact",
            variant: "secondary",
          }}
          aside={<AIConnectorDiagram />}
          banner={
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 bg-white border border-homeowners-border rounded-xl px-8 sm:px-12 lg:px-16 py-6 sm:py-8 lg:py-9">
              <span className="text-black font-montserrat text-sm sm:text-base lg:text-lg xl:text-xl text-center sm:text-left">
                {t("Banner.Text")}
              </span>
              <a
                href={sendolaPayUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-homeowners-purple font-montserrat font-semibold text-sm sm:text-base lg:text-lg xl:text-xl hover:opacity-80 transition-opacity flex-shrink-0"
              >
                {t("Banner.Link")}
                <ArrowTopRightOnSquareIcon className="w-5 h-5 lg:w-6 lg:h-6" />
              </a>
            </div>
          }
        />
      </div>

      <WhoWeServeSection />
      <WhoWeCanPaySection />
      <TestimonialSection />
      <WhoWePaySection />
      <ConciergeServiceSection />
      <PricingSection />
      <LevelUpSection />
      <ClientsSaySection />
      <FaqSection />
    </div>
  );
};

export default ForHomeownersPage;
